import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from './supabase'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)      // Supabase auth user
  const [profile, setProfile] = useState(null) // user_profiles row
  const [loading, setLoading] = useState(true)
  // True when this login is linked to a Personnel/auditor record (auditors.user_id). Drives "My Audits"
  // visibility for office/QM staff who are ALSO auditors (e.g. the QM who conducts audits).
  const [isLinkedAuditor, setIsLinkedAuditor] = useState(false)

  // Whose profile is loaded / loading — so the startup session read and the auth events (which fire together on
  // every page load) fetch it ONCE, not twice. A token refresh (hourly) still re-reads it quietly in the background.
  const loadedFor = useRef(null)
  const inFlight = useRef(null)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null)
      if (session?.user) loadProfile(session.user.id)
      else setLoading(false)
    })
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null)
      if (session?.user) loadProfile(session.user.id, event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED')
      else { loadedFor.current = null; setProfile(null); setIsLinkedAuditor(false); setLoading(false) }
    })
    return () => subscription.unsubscribe()
  }, [])

  async function loadProfile(userId, refresh = false) {
    if (inFlight.current && inFlight.current.userId === userId) return inFlight.current.promise   // already loading
    if (!refresh && loadedFor.current === userId) return                                         // already loaded
    const promise = (async () => {
      try {
        // Profile + linked-auditor check together (they used to run one after the other).
        const [{ data, error }, aud] = await Promise.all([
          supabase.from('user_profiles').select('*').eq('id', userId).maybeSingle(),
          // Read-only; auditors SELECT is open to authenticated. Best-effort.
          supabase.from('auditors').select('id').eq('user_id', userId).limit(1).maybeSingle().then(r => r, () => ({ data: null })),
        ])
        if (error) { console.warn('user_profiles table missing or error - run FIX_V18.sql:', error.message) }
        // Access turned off by the Admin (Settings → Users): log out here too; the login page says why.
        if (data && data.active === false) {
          try { sessionStorage.setItem('auditpm_access_off', '1') } catch (e) {}
          await supabase.auth.signOut()
          return
        }
        setProfile(data || null)
        setIsLinkedAuditor(!!aud?.data)
        loadedFor.current = userId
      } catch(e) { console.warn('AuthContext loadProfile error:', e) }
      setLoading(false)
    })()
    inFlight.current = { userId, promise }
    try { await promise } finally { if (inFlight.current?.promise === promise) inFlight.current = null }
  }

  // Helpers
  const role = profile?.role || null
  const isQM = role === 'Quality Manager (Primary)' || role === 'Quality Manager (Delegate)'
  const isOps = role === 'Operations Manager'
  const isSales = role === 'Sales Manager'
  const isAdmin = role === 'Admin'
  const isDocController = role === 'Document Controller'
  const isAdmin2 = role === 'Administrative Assistant'
  // Auditor is a SYSTEM-ACCESS role only (auditor eligibility/grade/CT17 live in the `auditors` table).
  const isAuditor = role === 'Auditor'
  // Can create/edit quotations: Sales, Ops, Doc Controller, Admin Assistant, Admin
  const canCreateQuote = isSales || isOps || isDocController || isAdmin2 || isAdmin || isQM
  // Can assign auditors: everyone except cannot approve
  const canAssign = canCreateQuote
  // Only QM can approve
  const canApprove = isQM || isAdmin
  // "My Audits" shows for the pure Auditor role OR any office/QM login linked to a Personnel record.
  const showMyAudits = isAuditor || isLinkedAuditor

  // Log an action to the audit trail
  async function logAction(action, entityType, entityId, entityLabel, details = {}) {
    if (!profile) return
    try {
      await supabase.from('audit_trail').insert({
      action,
      entity_type: entityType,
      entity_id: entityId,
      entity_label: entityLabel,
      performed_by: user?.id,
      performer_name: profile.name,
      performer_role: role,
      details,
      })
    } catch(e) { console.warn('audit_trail insert error (run FIX_V18.sql):', e) }
  }

  return (
    <AuthContext.Provider value={{ user, profile, role, isQM, isOps, isSales, isAdmin, isDocController, isAuditor, isLinkedAuditor, showMyAudits, canCreateQuote, canAssign, canApprove, loading, logAction }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
